import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import {
  PageContainer,
  PageHeader,
  Select,
  SelectFilter,
  toast,
} from "@outpave/ds";
import { Inline, Page, Stack } from "components/layout/reusableLayouts";
import ErrorState from "components/common/ErrorState";
import { getUserFacingErrorMessage } from "utils/graphql";
import {
  AddFundsDrawer,
  EarnRulesTable,
  ManageBankDrawer,
  ProgramSettings,
  PromotionsTimeline,
  RedemptionsTable,
  RewardRuleDrawer,
  RewardsByCompanyTable,
  RewardsFundingTable,
  RewardsPoolSummary,
  RewardsProgramSummary,
  TestPurchaseDrawer,
  isPoolUnderfunded,
  poolLabel,
} from "components/platform/rewards";
import type {
  CompletePlatformFundingSourceInput,
  PlatformFundingRequest,
  PlatformFundingSource,
  PlatformRewardRule,
  PlatformRewardRuleSummary,
  RewardCategory,
  RewardRuleScope,
  RewardRuleStatus,
} from "components/platform/rewards/types";
import {
  GET_PLATFORM_REWARD_RULES,
  GET_PLATFORM_REWARD_RULE_SUMMARY,
  GET_REWARD_CATEGORIES,
} from "graphql/platform";
import usePlatformRewards from "hooks/usePlatformRewards";
import { useConfirmPlatformBank } from "hooks/useConfirmPlatformBank";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import {
  REWARD_RULE_STATUS_LABEL,
  ruleTarget,
} from "utils/rewards/rewardRules";

type RewardsTab =
  "earn-rules" | "promotions" | "program-settings" | "redemptions" | "funding";

const SCOPES: RewardRuleScope[] = ["ALL", "CATEGORY", "MERCHANT"];
const STATUSES: RewardRuleStatus[] = ["LIVE", "SCHEDULED", "ENDED", "PAUSED"];
const SCOPE_LABEL: Record<RewardRuleScope, string> = {
  ALL: "All merchants",
  CATEGORY: "Category",
  MERCHANT: "Merchant",
};

const Rewards: React.FC = () => {
  const { t } = useTranslation();
  const {
    pools,
    poolsLoading,
    poolsError,
    refetchPools,
    fundingSources,
    sourcesLoading,
    sourcesError,
    beginFundingSource,
    completeFundingSource,
    removeFundingSource,
    fundRewards,
    saving,
  } = usePlatformRewards();
  const confirmBank = useConfirmPlatformBank();
  const canManageFunding = useCheckUserRole(Role.PlatformOwner);
  const ownedPools = pools.filter((pool) => pool.owned);
  const [activePoolId, setActivePoolId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [refreshDataIndex, setRefreshDataIndex] = useState(0);
  const [tab, setTab] = useState<RewardsTab>("earn-rules");
  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [editing, setEditing] = useState<PlatformRewardRule | null>(null);
  const [ruleDrawerOpen, setRuleDrawerOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const rulesQuery = useQuery<{ platformRewardRules: PlatformRewardRule[] }>(
    GET_PLATFORM_REWARD_RULES,
    { fetchPolicy: "network-only" },
  );
  const categoriesQuery = useQuery<{ rewardCategories: RewardCategory[] }>(
    GET_REWARD_CATEGORIES,
  );
  const rules = useMemo(
    () => rulesQuery.data?.platformRewardRules ?? [],
    [rulesQuery.data],
  );
  const categories = categoriesQuery.data?.rewardCategories ?? [];
  const summaryPool =
    pools.find(
      (pool) => `${pool.platformAccountId}#${pool.currency}` === activePoolId,
    ) ??
    pools[0] ??
    null;
  const summaryQuery = useQuery<{
    platformRewardRuleSummary: PlatformRewardRuleSummary;
  }>(GET_PLATFORM_REWARD_RULE_SUMMARY, {
    skip: !summaryPool,
    variables: summaryPool
      ? {
          platformAccountId: summaryPool.platformAccountId,
          currency: summaryPool.currency,
        }
      : undefined,
    fetchPolicy: "network-only",
  });
  const summary = summaryQuery.data?.platformRewardRuleSummary ?? null;
  const filterLabels = useMemo(
    () => ({
      search: (label: string) =>
        t("SelectFilterSearch", { label: label.toLowerCase() }),
      noMatches: (label: string, query: string) =>
        t("SelectFilterNoMatches", { label: label.toLowerCase(), query }),
      available: (total: number) => t("SelectFilterAvailable", { total }),
    }),
    [t],
  );
  const visibleRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rules.filter(
      (rule) =>
        (!scopeFilter.length || scopeFilter.includes(rule.scope)) &&
        (!statusFilter.length || statusFilter.includes(rule.status)) &&
        (!term ||
          rule.name.toLowerCase().includes(term) ||
          ruleTarget(rule, t).toLowerCase().includes(term)),
    );
  }, [rules, scopeFilter, statusFilter, search, t]);
  if (poolsError && !poolsLoading && pools.length === 0) {
    return (
      <Page>
        <ErrorState
          title={t("Something went wrong")}
          description={t("RewardsLoadError")}
          retryLabel={t("Retry")}
          onRetry={refetchPools}
        />
      </Page>
    );
  }

  const poolKey = (pool: { platformAccountId: string; currency: string }) =>
    `${pool.platformAccountId}#${pool.currency}`;
  const activePool =
    pools.find((pool) => poolKey(pool) === activePoolId) ?? pools[0] ?? null;
  // Funding goes to the bank's own pool, so only the banks of the pool in view are offered
  const activeSources = fundingSources.filter(
    (source) =>
      source.status === "ACTIVE" &&
      source.destinationAccountId === activePool?.platformAccountId,
  );

  const showError = (error: unknown) =>
    toast.error(
      getUserFacingErrorMessage(
        error,
        t("SomethingWentWrongDescriptionWithSupport"),
      ),
    );

  const handleAdd = async (input: {
    destinationAccountId: string;
    name: string;
  }): Promise<PlatformFundingSource | undefined> => {
    try {
      const { data } = await beginFundingSource({ variables: input });
      return data?.beginPlatformFundingSource ?? undefined;
    } catch (error) {
      showError(error);
      return undefined;
    }
  };

  const handleComplete = async (
    id: string,
    input: CompletePlatformFundingSourceInput,
  ): Promise<PlatformFundingSource | undefined> => {
    try {
      const { data } = await completeFundingSource({
        variables: { id, input },
      });
      toast.success(t("Funding bank confirmed"));
      return data?.completePlatformFundingSource ?? undefined;
    } catch (error) {
      showError(error);
      return undefined;
    }
  };

  const handleConfirmBank: typeof confirmBank = async (
    clientSecret,
    details,
    publishableKey,
  ) => {
    try {
      return await confirmBank(clientSecret, details, publishableKey);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("SomethingWentWrongDescriptionWithSupport"),
      );
      throw error;
    }
  };

  const handleFund = async (input: PlatformFundingRequest) => {
    try {
      await fundRewards({ variables: { input } });
      toast.success(t("Funding requested"));
      setRefreshDataIndex((index) => index + 1);
      return true;
    } catch (error) {
      showError(error);
      return false;
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await removeFundingSource({ variables: { id } });
      toast.success(t("Funding bank removed"));
      setRefreshDataIndex((index) => index + 1);
    } catch (error) {
      showError(error);
    }
  };

  const openRule = (rule: PlatformRewardRule | null) => {
    setEditing(rule);
    setRuleDrawerOpen(true);
  };

  const handleRuleSaved = () => {
    rulesQuery.refetch();
    summaryQuery.refetch();
  };

  const tabs: { id: RewardsTab; label: string }[] = [
    { id: "earn-rules", label: t("Earn rules") },
    { id: "promotions", label: t("Promotions") },
    { id: "program-settings", label: t("Program settings") },
    { id: "redemptions", label: t("Redemptions") },
    { id: "funding", label: t("Funding") },
  ];

  const actions =
    tab === "funding" ? (
      canManageFunding && (
        <>
          <Button
            variant="primary-outlined"
            onClick={() => setDrawerOpen(true)}
          >
            {t("Funding sources")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={() => setAddFundsOpen(true)}
            disabled={ownedPools.length === 0}
          >
            {t("Add funds")}
          </Button>
        </>
      )
    ) : (
      <>
        <Button variant="primary-outlined" onClick={() => setTestOpen(true)}>
          {t("Simulate rules")}
        </Button>
        {canManageFunding && (
          <Button variant="primary-filled" onClick={() => openRule(null)}>
            {t("New earn rule")}
          </Button>
        )}
      </>
    );

  return (
    <Page sx={{ px: 0, pb: 0 }}>
      <Stack gap="var(--space-6)">
        <Stack gap="var(--space-6)" sx={{ px: "var(--space-7)" }}>
          {isPoolUnderfunded(activePool) && activePool && (
            <Alert severity="warning">
              {t("PoolCoverageWarning", {
                percent: activePool.coveragePercent,
                threshold: activePool.coverageThresholdPercent,
              })}
            </Alert>
          )}
          <PageHeader
            title={t("Rewards")}
            actions={
              <Inline gap="var(--space-3)" align="center">
                {pools.length > 1 && (
                  <Select
                    aria-label={t("Rewards pool")}
                    fullWidth={false}
                    width="min(360px, 100%)"
                    size="md"
                    options={pools.map((pool) => ({
                      id: poolKey(pool),
                      label: poolLabel(pool),
                    }))}
                    value={activePool ? poolKey(activePool) : null}
                    onChange={(id) => id && setActivePoolId(id)}
                    clearable={false}
                  />
                )}
                {actions}
              </Inline>
            }
          />
          <RewardsProgramSummary
            summary={summary}
            pool={activePool}
            loading={summaryQuery.loading || poolsLoading}
          />
        </Stack>
        <PageContainer
          tabs={{
            items: tabs,
            activeId: tab,
            onChange: (id) => setTab(id as RewardsTab),
          }}
          search={
            tab === "earn-rules"
              ? {
                  value: search,
                  onChange: setSearch,
                  placeholder: t("Search rules"),
                }
              : undefined
          }
          filters={
            tab === "earn-rules" ? (
              <>
                <SelectFilter
                  multiple
                  label={t("Scope")}
                  options={SCOPES.map((scope) => ({
                    value: scope,
                    label: t(SCOPE_LABEL[scope]),
                  }))}
                  selected={scopeFilter}
                  onChange={setScopeFilter}
                  labels={filterLabels}
                />
                <SelectFilter
                  multiple
                  label={t("Status")}
                  options={STATUSES.map((status) => ({
                    value: status,
                    label: t(REWARD_RULE_STATUS_LABEL[status]),
                  }))}
                  selected={statusFilter}
                  onChange={setStatusFilter}
                  labels={filterLabels}
                />
              </>
            ) : undefined
          }
        >
          {tab === "earn-rules" && (
            <EarnRulesTable
              rules={visibleRules}
              loading={rulesQuery.loading}
              error={Boolean(rulesQuery.error)}
              emptyState={rules.length ? t("No rules match") : t("NoEarnRules")}
              onOpen={openRule}
            />
          )}
          {tab === "promotions" && (
            <PromotionsTimeline
              rules={rules}
              loading={rulesQuery.loading}
              error={Boolean(rulesQuery.error)}
              onOpen={openRule}
            />
          )}
          {tab === "program-settings" && (
            <Stack sx={{ padding: "var(--space-5)" }}>
              <ProgramSettings summary={summary} />
            </Stack>
          )}
          {tab === "redemptions" && activePool && (
            <RedemptionsTable
              key={poolKey(activePool)}
              platformAccountId={activePool.platformAccountId}
            />
          )}
          {tab === "funding" && (
            <Stack gap="var(--space-6)" sx={{ padding: "var(--space-5)" }}>
              <RewardsPoolSummary pool={activePool} loading={poolsLoading} />
              <RewardsByCompanyTable pool={activePool} />
              {activePool && (
                <RewardsFundingTable
                  key={poolKey(activePool)}
                  platformAccountId={activePool.platformAccountId}
                  refreshDataIndex={refreshDataIndex}
                />
              )}
            </Stack>
          )}
        </PageContainer>
      </Stack>
      <RewardRuleDrawer
        open={ruleDrawerOpen}
        onClose={() => setRuleDrawerOpen(false)}
        rule={editing}
        rules={rules}
        categories={categories}
        currency={activePool?.currency ?? "usd"}
        canEdit={canManageFunding}
        onSaved={handleRuleSaved}
      />
      <TestPurchaseDrawer
        open={testOpen}
        onClose={() => setTestOpen(false)}
        categories={categories}
      />
      <ManageBankDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        pools={ownedPools}
        fundingSources={fundingSources}
        loading={sourcesLoading}
        error={Boolean(sourcesError)}
        saving={saving}
        onAdd={handleAdd}
        onConfirmBank={handleConfirmBank}
        onComplete={handleComplete}
        onRemove={handleRemove}
      />
      <AddFundsDrawer
        open={addFundsOpen}
        onClose={() => setAddFundsOpen(false)}
        sources={activeSources}
        saving={saving}
        onFund={handleFund}
      />
    </Page>
  );
};

export default Rewards;
